/*
 * Privacy policy + data-disclosure page.
 *
 * Routed at /privacy on the SWA build. Each public Squarespace tool
 * also links to the same content (hosted at the SWA URL — that's the
 * canonical privacy notice for the suite).
 *
 * Content here describes the actual technical surface this codebase
 * implements: what's captured, why, how long it's retained, and the
 * user's GDPR/CCPA rights. Update this in lockstep with backend
 * changes to submission metadata or retention policy.
 *
 * This file intentionally avoids legalese. The audience is in-house
 * legal — they read these critically. Plain language wins trust.
 */
import './privacy.css';

export const PRIVACY_VERSION = 'v1.0 — 2026-05-06';

export function PrivacyPolicy() {
  return (
    <main className="page-shell privacy">
      <header className="privacy-header">
        <h1>Privacy &amp; Data Use</h1>
        <p className="lede">
          What we collect when you use the mot-r diagnostic tools, why we collect it,
          and the choices you have. Plain language; no legalese.
        </p>
        <p className="privacy-version">
          Effective: {PRIVACY_VERSION}
        </p>
      </header>

      <section>
        <h2>The short version</h2>
        <ul>
          <li>The diagnostic tools are <strong>anonymous by default</strong>. We don't ask
            for your name, your company, or your email.</li>
          <li>We assign your browser an <strong>anonymous ID</strong> so your in-progress
            answers persist across sessions and so we can stitch together your usage of
            multiple tools (e.g., Plan Builder and the Workbench).</li>
          <li>We capture <strong>technical metadata</strong> useful for analytics — browser
            family, country, language — but never your raw IP address or any cross-site
            tracking pixels.</li>
          <li>We retain submissions for <strong>24 months</strong>, after which they're
            anonymized; behavioural events are deleted after <strong>12 months</strong>.</li>
          <li>You can <strong>request a copy of your data</strong> or have it{' '}
            <strong>deleted</strong> at any time. See{' '}
            <a href="#your-rights">Your rights</a>.</li>
        </ul>
      </section>

      <section>
        <h2 id="what-we-collect">What we collect</h2>

        <h3>Diagnostic answers</h3>
        <p>
          The questions you answer in each tool — friction phase, outcomes, vendor names,
          flexibility level, and so on. This is the substance of the diagnostic and is
          stored verbatim. <strong>We don't ask for personal information in these
          fields.</strong> If you choose to type a colleague's name or your employer's
          name into a free-text answer, that text will be stored as you typed it.
        </p>

        <h3>Anonymous identifiers</h3>
        <ul>
          <li>
            <strong>Browser ID</strong> — a random UUID we generate and store in your
            browser's local storage under <code>motr_client_id</code>. It identifies your
            browser, not you. It lets you return to a tool later without losing progress
            and lets us count "buyers who used both Plan Builder and the Workbench" in
            aggregate.
          </li>
          <li>
            <strong>Hashed IP address</strong> — your IP address is one-way hashed
            (SHA-256 with a rotating salt) on arrival, and only the hash is stored.
            Your raw IP is never written to the database. The hash is used to detect
            abusive request patterns; it can't be reversed back to your IP.
          </li>
        </ul>

        <h3>Technical metadata</h3>
        <p>Captured per submission, all derived signals — none of these are PII on their own:</p>
        <ul>
          <li><strong>Browser family + major version</strong> (e.g., "Chrome 119")</li>
          <li><strong>Operating system family</strong> (e.g., "Mac OS X")</li>
          <li><strong>Device type</strong> (desktop / mobile / tablet)</li>
          <li><strong>Country</strong> (ISO country code, e.g., "US") — derived from your
            connection by our edge network. <strong>No city, no neighbourhood, no
            geolocation.</strong></li>
          <li><strong>Browser language</strong> (e.g., "en-US") — from the standard
            <code>Accept-Language</code> header</li>
          <li><strong>Whether this is your first visit</strong> — derived from your
            browser ID</li>
          <li><strong>Time spent</strong> from opening the tool to completing it</li>
          <li><strong>Referring page</strong> — only the origin (e.g., your company's
            intranet domain), not the full URL or any query parameters, because we set
            the <code>strict-origin-when-cross-origin</code> referrer policy</li>
        </ul>

        <h3>Behavioural events</h3>
        <p>
          When you start a tool, complete a phase, add a vendor, or finish a diagnostic,
          we record an event with the action name and your anonymous browser ID. These
          help us understand where buyers drop off in the flow. They're capped at 8 KB
          per event and deleted after 12 months.
        </p>

        <h3>What we don't collect</h3>
        <ul>
          <li>Your name, email address, or phone number — we never ask</li>
          <li>Your raw IP address — we hash it on arrival</li>
          <li>Your precise location, GPS, or city</li>
          <li>Cross-site tracking cookies or third-party tracking pixels</li>
          <li>Browser fingerprints (canvas, WebGL, audio, etc.)</li>
          <li>Anything from social-media networks</li>
        </ul>
      </section>

      <section>
        <h2 id="how-we-use">How we use what we collect</h2>
        <ul>
          <li>
            <strong>Show you your own results.</strong> The submission you make is what
            generates your improvement plan, vendor scorecard, or stay-or-go decision.
          </li>
          <li>
            <strong>Save your progress.</strong> Browser local storage holds your
            in-progress answers so you can leave and come back without retyping.
          </li>
          <li>
            <strong>Aggregate analytics.</strong> Counts and distributions across all
            buyers — friction phase distribution, vendor frequency, country breakdown —
            help us improve the tools. None of these aggregates can be traced back to
            an individual.
          </li>
          <li>
            <strong>Cross-tool stitching.</strong> If you use Plan Builder and then the
            Workbench, your anonymous browser ID lets us see them as one buyer journey
            in our analytics — never named, never identified.
          </li>
          <li>
            <strong>Fraud and abuse prevention.</strong> The hashed IP and rate-limit
            signals are used to block obvious flood / scraping behavior.
          </li>
        </ul>
      </section>

      <section>
        <h2 id="cookies">Cookies and local storage</h2>
        <p>
          We don't set HTTP cookies. We do use your browser's <strong>local storage</strong>{' '}
          (a per-domain key/value store) for:
        </p>
        <ul>
          <li><code>motr_client_id</code> — your anonymous browser UUID (described above)</li>
          <li>
            Tool-specific state keys (e.g., <code>motr-ddw-state-v1</code>,{' '}
            <code>motr-lens-v1</code>) — your in-progress answers so you can return
            to the tool without losing progress
          </li>
          <li><code>motr_outbound_queue_v1</code> — submissions that failed to send (e.g.,
            during a network drop) and are queued for retry on your next visit</li>
          <li><code>motr_privacy_dismissed_v1</code> — a flag that records you've
            dismissed the privacy notice on this browser</li>
        </ul>
        <p>
          You can clear local storage at any time from your browser's developer tools
          (Application tab → Local Storage → motr-tools.* → Clear). Doing so erases your
          anonymous browser ID and all in-progress answers.
        </p>
      </section>

      <section>
        <h2 id="retention">How long we keep things</h2>
        <ul>
          <li><strong>Submissions:</strong> 24 months. After that, free-text fields are
            replaced with the literal string <code>[ANONYMIZED]</code>; user agent,
            referrer, and IP hash are dropped. Aggregate-level fields (friction phase,
            country, browser family) remain so historical analytics still work, but
            no row can be traced to a buyer.</li>
          <li><strong>Behavioural events:</strong> 12 months, after which they're deleted
            entirely.</li>
          <li><strong>Consent records:</strong> retained as long as the underlying
            submission remains, then deleted with it.</li>
          <li><strong>Audit log of GDPR actions</strong> (exports, erasures, retention
            sweeps): retained indefinitely, but contains only action metadata — never
            the data itself.</li>
        </ul>
        <p>
          A scheduled job runs nightly to apply these retention rules automatically.
        </p>
      </section>

      <section>
        <h2 id="your-rights">Your rights</h2>
        <p>
          Whether you're covered by GDPR, CCPA, or another regime, the following are
          available on request:
        </p>
        <ul>
          <li>
            <strong>Right to access / export.</strong> We'll send you a complete JSON
            dump of every submission, consent record, and behavioural event tied to
            your anonymous browser ID. Because the data is anonymous, you'll need to
            tell us your <code>motr_client_id</code> (find it under your browser's
            Local Storage developer tab).
          </li>
          <li>
            <strong>Right to erasure / "be forgotten".</strong> We anonymize all
            submissions tied to your ID in place — the rows remain (so aggregate
            counts still work), but every free-text answer is replaced with{' '}
            <code>[ANONYMIZED]</code>, the user agent / referrer / IP hash are dropped,
            and your client record is marked deleted. Consent records are redacted at
            the same time.
          </li>
          <li>
            <strong>Right to rectify.</strong> Re-take the tool and re-submit with the
            same browser; the new submission supersedes the prior one (the
            <code>client_submission_id</code> is stable per browser).
          </li>
          <li>
            <strong>Right to withdraw consent.</strong> If you've opted into the
            research questions, you can withdraw at any time by emailing the address
            below.
          </li>
        </ul>
        <p>
          To exercise any of these, email <a href="mailto:privacy@motr.example">privacy@motr.example</a>{' '}
          with your <code>motr_client_id</code>. We respond within 30 days.
        </p>
      </section>

      <section>
        <h2 id="changes">Changes to this policy</h2>
        <p>
          When we change what's captured or how it's used, we'll bump the version at
          the top of this page and require fresh consent on any opt-in flows. The
          version that applied at the time of your submission is stored alongside it
          for audit.
        </p>
      </section>

      <section className="privacy-contact">
        <h2>Contact</h2>
        <p>
          For privacy questions, exercise of rights, or anything else about how we
          handle data: <a href="mailto:privacy@motr.example">privacy@motr.example</a>
        </p>
      </section>
    </main>
  );
}
